/**
 * home-hero-content.ts
 * ─────────────────────────────────────────────────────────────────
 * Hero homepage contextualisé par audience.
 * Chaque variante : bénéfice d'abord, langage direct, pas de jargon.
 */

import type { AudienceId } from '@shared/constants/audiences';

export interface HeroContent {
  eyebrow: string;
  title: string;
  titleAccent: string;
  sub: string;
  chip: string;
  primaryCta: { label: string; href: string; iconName?: string };
  secondaryCta: { label: string; href: string };
}

type HeroContentMap = Partial<Record<AudienceId, HeroContent>> & { default: HeroContent };

export const HOME_HERO_CONTENT: HeroContentMap = {
  default: {
    eyebrow: 'Primes énergie, Factures allégées, Travaux financés',
    title: 'Vos primes vous attendent.',
    titleAccent: 'On vous aide à les toucher.',
    sub: 'Quel que soit votre profil, on sait quelles aides existent pour vous et comment les obtenir. Diagnostic gratuit, réponse chiffrée, aucun frais caché.',
    chip: '60 secondes, sans inscription, résultat immédiat',
    primaryCta: { label: 'Voir nos outils gratuits', href: '/outils', iconName: 'layout-grid' },
    secondaryCta: { label: 'Choisir mon profil', href: '/#audiences' },
  },

  particuliers: {
    eyebrow: 'Propriétaires, Bailleurs, Locataires',
    title: 'Rénovez sans avancer',
    titleAccent: 'un centime.',
    sub: "On cumule toutes les aides auxquelles vous avez droit et on vous présente un artisan RGE de confiance. Le devis inclut déjà les aides déduites. Vous payez le reste, c'est tout.",
    chip: 'Zéro avance, Artisan RGE, Devis sous 48 h',
    primaryCta: {
      label: 'Calculer mes aides',
      href: '/particuliers/calculer-mes-aides',
      iconName: 'calculator',
    },
    secondaryCta: { label: 'Voir des rénovations réussies', href: '/cas-clients' },
  },

  professionnels: {
    eyebrow: 'TPE, PME, Indépendants, Professions libérales',
    title: 'Payez moins cher',
    titleAccent: 'vos factures pro.',
    sub: 'Comparez ce que vous payez avec ce que propose le marché. On analyse votre contrat actuel ligne par ligne et on vous dit honnêtement si ça vaut le coup de changer. Si oui, on négocie pour vous.',
    chip: 'Audit gratuit, 5 min, sans engagement',
    primaryCta: {
      label: 'Auditer mes factures',
      href: '/professionnels/comparateur-energie',
      iconName: 'file-search',
    },
    secondaryCta: { label: 'Estimer mes primes', href: '/professionnels/diagnostic-cee' },
  },

  entreprises: {
    eyebrow: 'ETI, Grands comptes, Multi-sites',
    title: 'Une stratégie énergie',
    titleAccent: 'sur mesure pour votre entreprise.',
    sub: "Un interlocuteur unique pour tous vos sites. On audite, on compare les offres, on négocie vos contrats d'énergie et on sécurise vos obligations légales. Référent dédié, confidentialité garantie.",
    chip: 'Référent dédié, Confidentialité, Réponse sous 48 h',
    primaryCta: {
      label: 'Demander une étude chiffrée',
      href: '/contact?source=entreprise',
      iconName: 'handshake',
    },
    secondaryCta: { label: 'Audit obligation tertiaire', href: '/entreprises/decret-tertiaire' },
  },

  tertiaire: {
    eyebrow: 'Bureaux, Commerces, Hôtels, à partir de 1 000 m²',
    title: 'Votre obligation -40 %',
    titleAccent: 'prise en charge de A à Z.',
    sub: "Vous devez réduire la consommation de votre bâtiment de 40 % d'ici 2030. On calcule votre trajectoire, on planifie les travaux et on les finance avec les primes disponibles.",
    chip: 'Trajectoire -40 %, Primes pour financer, Déclaration incluse',
    primaryCta: {
      label: 'Auditer mon bâtiment',
      href: '/tertiaire/decret-tertiaire',
      iconName: 'gauge',
    },
    secondaryCta: { label: "Comprendre l'obligation", href: '/tertiaire' },
  },

  industrie: {
    eyebrow: 'Industriels, Sites de production',
    title: 'Vos consommations industrielles',
    titleAccent: 'auditées et optimisées.',
    sub: "Audit énergétique obligatoire, primes sur vos équipements, subventions cumulables. On identifie ce qui coûte cher et ce qui peut être financé. Avec un bureau d'études thermique sur chaque dossier.",
    chip: 'Audit obligatoire, Primes équipements, Aides publiques',
    primaryCta: {
      label: 'Estimer mes primes',
      href: '/industrie/diagnostic-cee',
      iconName: 'scan-line',
    },
    secondaryCta: { label: 'Prendre rendez-vous', href: '/contact?source=industrie' },
  },

  collectivites: {
    eyebrow: 'Mairies, EPCI, Bailleurs sociaux',
    title: 'Financez vos bâtiments publics',
    titleAccent: 'sans alourdir le budget.',
    sub: "On cumule les aides de l'État, les fonds régionaux et les primes énergie pour réduire le coût de vos travaux au maximum. Conforme au code de la commande publique, référencé UGAP.",
    chip: 'Référencé UGAP, Marchés conformes, Pré-étude offerte',
    primaryCta: {
      label: 'Recevoir ma pré-étude gratuite',
      href: '/contact?source=collectivite',
      iconName: 'file-search',
    },
    secondaryCta: { label: 'Audit obligation tertiaire', href: '/collectivites/decret-tertiaire' },
  },

  coproprietes: {
    eyebrow: 'Syndics, Conseils syndicaux, Copropriétaires',
    title: 'Faites voter la rénovation',
    titleAccent: 'de votre immeuble.',
    sub: "On prépare tout pour votre AG : diagnostic de l'immeuble, chiffrage des aides par lot, présentation aux copropriétaires. L'objectif : un vote positif dès la première convocation.",
    chip: 'Présentation AG offerte, Vote en 1re convocation',
    primaryCta: {
      label: 'Préparer mon AG',
      href: '/contact?source=copropriete',
      iconName: 'clipboard-check',
    },
    secondaryCta: { label: 'Calculer les aides Copro', href: '/coproprietes/calculer-mes-aides' },
  },

  artisans: {
    eyebrow: 'Artisans RGE, Partenaires CEE',
    title: 'Vos primes CEE',
    titleAccent: 'sans risque ni paperasse.',
    sub: "Vous faites vos chantiers, on monte les dossiers de primes. On dépose, on suit, on défend en cas de contrôle. Si le dossier est rejeté, vous ne payez rien. C'est aussi simple que ça.",
    chip: "0 € d'avance, Paiement 30-60 j, Mandat en 5 min",
    primaryCta: {
      label: 'Devenir partenaire',
      href: '/devenir-partenaire/candidater?profile=artisan',
      iconName: 'hard-hat',
    },
    secondaryCta: { label: 'Voir comment ça marche', href: '/artisans#methode' },
  },

  agriculteurs: {
    eyebrow: 'Exploitations, Élevage, Viticulture, Céréales',
    title: 'Financez vos bâtiments',
    titleAccent: "d'exploitation.",
    sub: 'Hangars, étables, serres : on vous dit exactement quelles aides existent pour vous et combien vous pouvez toucher. Diagnostic gratuit, sans engagement.',
    chip: "Primes énergie, Aides régionales, 0 € d'avance",
    primaryCta: {
      label: 'Diagnostiquer mon exploitation',
      href: '/agriculteurs/diagnostic-cee',
      iconName: 'scan-line',
    },
    secondaryCta: { label: 'Voir les aides disponibles', href: '/cee/fiches?secteur=AGRI' },
  },
};

export const AUDIENCE_BLOG_TAGS: Partial<Record<AudienceId, string[]>> & { default: string[] } = {
  default: [],
  particuliers: ['maprimerenov', 'cee', 'renovation', 'pac', 'isolation'],
  professionnels: ['factures-energie', 'cee-tertiaire', 'courtage', 'turpe'],
  entreprises: ['decret-tertiaire', 'cee-industriels', 'hta', 'iso-50001'],
  tertiaire: ['decret-tertiaire', 'operat', 'bacs', 'cee-bat', 'gtb'],
  industrie: [
    'cee-industriels',
    'iso-50001',
    'audit-nf-en-16247',
    'fonds-chaleur',
    'decarbonation',
  ],
  collectivites: ['decret-tertiaire', 'fonds-vert', 'bacs', 'ugap'],
  coproprietes: ['mpr-copro', 'cee-collectifs', 'eco-ptz', 'ag'],
  artisans: ['cee', 'rge', 'coup-de-pouce', 'mandataire-cee'],
  agriculteurs: [
    'cee-agri',
    'batiment-exploitation',
    'pcae',
    'franceagrimer',
    'photovoltaique-agri',
    'pac-agri',
  ],
};
